/**
 * Report history — every version this account may see, newest first.
 *
 * Versions, not reports: correcting a measurement never rewrites the PDF a
 * student already has, so August can hold a v1 and a v2 and both stay here
 * (Architecture §13). The list shows what was sent and when, because that is
 * the question a studio actually asks of it.
 *
 * Two filters, because the two ways a studio comes looking are "what did I
 * send Ayşe?" and "what went out last month". A report can also be destroyed
 * from here, behind the admin gate: a report built on the wrong student is the
 * one mistake immutability cannot fix on its own. Delete takes the whole
 * report and every version of it (API §18.5) — leaving one of two versions
 * behind would serve nobody.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateLong, formatPeriod } from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { AdminReauthDialog } from '../components/ui/AdminReauthDialog.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { useApiData } from '../hooks/useApiData.js';
import { useSession } from '../session/SessionContext.js';
import './ReportHistory.css';

interface ReportVersion {
  id: string;
  reportId: string;
  versionNumber: number;
  status: 'rendering' | 'ready' | 'failed';
  failureReason: string | null;
  pdfUrl: string | null;
  generatedAt: string;
  studentId: string;
  studentName: string;
  periodStart: string;
  periodEnd: string;
  instructorName: string | null;
}

interface ReportListResponse {
  items: ReportVersion[];
  pagination: { limit: number; offset: number; total: number };
}

const PAGE_SIZE = 25;

const MONTH_NAMES = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

/** The last twelve months, newest first — the span a studio ever browses. */
function recentMonths(): { value: string; label: string }[] {
  const now = new Date();
  const months: { value: string; label: string }[] = [];

  for (let back = 0; back < 12; back += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - back, 1);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    months.push({
      value: `${date.getFullYear()}-${month}`,
      label: `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`,
    });
  }

  return months;
}

export function ReportHistory() {
  const { session, hasAdminAccess } = useSession();
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState('');

  const [pendingDelete, setPendingDelete] = useState<ReportVersion | null>(null);
  const [reauthFor, setReauthFor] = useState<(() => void) | null>(null);
  const [actionError, setActionError] = useState<string>();

  const months = useMemo(recentMonths, []);
  const isOwner = session?.role === 'owner';

  const query = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(offset),
  });
  if (search.trim()) query.set('search', search.trim());
  if (month) {
    // The select's values are built as 'YYYY-MM' just above, so both halves
    // are always there; the API takes them apart (API §18.1).
    const [year = '', monthNumber = ''] = month.split('-');
    query.set('year', year);
    query.set('month', String(Number(monthNumber)));
  }

  const { data, error, loading, reload } = useApiData<ReportListResponse>(
    `/api/reports?${query.toString()}`,
  );

  const items = data?.items ?? [];
  const total = data?.pagination.total ?? 0;
  const filtered = search.trim() !== '' || month !== '';

  /** Filters change what the page shows, so they reset the page. */
  function changeFilter(apply: () => void) {
    apply();
    setOffset(0);
  }

  async function destroy(version: ReportVersion) {
    setActionError(undefined);
    try {
      await api.delete(`/api/reports/${version.reportId}/permanent`);
      reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  /** Runs now if the admin window is open, otherwise buys ten minutes first. */
  function withAdmin(action: () => void) {
    if (hasAdminAccess()) action();
    else setReauthFor(() => action);
  }

  return (
    <section className="history">
      <div className="history__head">
        <h1 className="page__title">Raporlar</h1>
        <Link to="/rapor-olustur" className="history__cta">
          Rapor Oluştur
        </Link>
      </div>

      <div className="history__filters">
        <input
          className="history__search"
          type="search"
          value={search}
          placeholder="Öğrenci ara"
          aria-label="Öğrenci ara"
          onChange={(event) => changeFilter(() => setSearch(event.target.value))}
        />
        <select
          className="history__month"
          value={month}
          aria-label="Ay"
          onChange={(event) => changeFilter(() => setMonth(event.target.value))}
        >
          <option value="">Tüm aylar</option>
          {months.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {filtered && (
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              changeFilter(() => {
                setSearch('');
                setMonth('');
              })
            }
          >
            Filtreyi Temizle
          </Button>
        )}
      </div>

      {error && <p role="alert">{error}</p>}
      {actionError && <p role="alert">{actionError}</p>}
      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && !filtered && (
        <div className="profile__empty">
          <p>Henüz rapor oluşturulmamış.</p>
          <Link to="/rapor-olustur" className="history__cta">
            İlk Raporu Oluştur
          </Link>
        </div>
      )}

      {!loading && items.length === 0 && filtered && (
        <div className="profile__empty">
          <p>Bu filtreye uyan rapor yok.</p>
        </div>
      )}

      {items.length > 0 && (
        <ul className="history__list">
          {items.map((version) => (
            <li key={version.id} className="history__card">
              <div className="history__identity">
                <Link
                  to={`/ogrenciler/${version.studentId}`}
                  className="history__student"
                >
                  {version.studentName}
                </Link>
                <span className="history__period">
                  {formatPeriod(version.periodStart, version.periodEnd)}
                </span>
                <span className="history__meta">
                  {formatDateLong(version.generatedAt.slice(0, 10))}
                  {version.instructorName ? ` · ${version.instructorName}` : ''}
                  {/* A first version needs no number; a second one does. */}
                  {version.versionNumber > 1 ? ` · Sürüm ${version.versionNumber}` : ''}
                </span>
              </div>

              <div className="history__actions">
                {version.status === 'ready' && version.pdfUrl && (
                  <a
                    className="history__open"
                    href={version.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    PDF'i Aç
                  </a>
                )}

                {/* A generation that failed is shown rather than hidden: the
                    studio needs to know that report never went anywhere. */}
                {version.status !== 'ready' && (
                  <span className="history__failed">
                    {version.status === 'rendering'
                      ? 'Oluşturuluyor…'
                      : 'Oluşturulamadı — yeniden deneyin'}
                  </span>
                )}

                {isOwner && (
                  <button
                    type="button"
                    className="history__delete"
                    onClick={() => setPendingDelete(version)}
                  >
                    Sil
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <div className="history__pager">
          <Button
            type="button"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Önceki
          </Button>
          <span className="history__count">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} / {total}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Sonraki
          </Button>
        </div>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Raporu sil"
          body={`${pendingDelete.studentName} için ${formatPeriod(pendingDelete.periodStart, pendingDelete.periodEnd)} dönemine ait rapor, tüm sürümleriyle ve PDF dosyalarıyla birlikte kalıcı olarak silinir. Öğrencinin ölçüm, fotoğraf ve değerlendirme kayıtları etkilenmez.`}
          confirmLabel="Kalıcı Olarak Sil"
          destructive
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            const version = pendingDelete;
            setPendingDelete(null);
            withAdmin(() => void destroy(version));
          }}
        />
      )}

      {reauthFor && (
        <AdminReauthDialog
          action="Raporu kalıcı olarak silmek"
          onCancel={() => setReauthFor(null)}
          onConfirmed={() => {
            const action = reauthFor;
            setReauthFor(null);
            action();
          }}
        />
      )}
    </section>
  );
}
